import axios from "axios";
import { getApiBaseUrl, getHeaders } from "./apiClient";
import { BackendApiResponse } from "./adminService";
import type { ToolCallLog } from "./ecommerceScrapeService";

// Types

export type FacebookGroupStatus = "active" | "paused" | "error";

export type FacebookGroup = {
  _id: string;
  url: string;
  name: string;
  status: FacebookGroupStatus;
  isActive?: boolean;
  notes?: string;
  allowExternalLinksInComments?: boolean;
  scrapeConfig: {
    maxPosts: number;
    minLikes: number;
    onlyPostsNewerThan?: string;
    processComments?: boolean;
    deepScrapeMinComments?: number;
    maxCommentsPerPost?: number;
    maxDeepScrapesPerRun?: number;
    adaptive?: {
      enabled?: boolean;
      emaDailyNewPosts?: number;
      currentLimit?: number;
    };
  };
  cursor?: {
    lastPostDate?: string;
    lastRunId?: string;
  };
  lastScrapedAt?: string;
  lastError?: string;
  stats: {
    totalRuns: number;
    totalPostsProcessed: number;
    totalReviewsCreated: number;
  };
  createdAt: string;
  updatedAt: string;
};

export type ScrapeRunStatus = "pending" | "processing" | "completed" | "failed";

export type { ToolCallLog };

export type ScrapeRun = {
  _id: string;
  groupId?: string | { _id: string; name: string; url: string };
  status: ScrapeRunStatus;
  entryType?: "group" | "standalone";
  adminNotes?: string;
  stats: {
    totalPostsFromApify?: number;  // new field
    totalPosts?: number;           // old field (backward compat)
    duplicatesSkipped?: number;
    newPostsProcessed?: number;
    classifiedAsReview: number;
    reviewsCreated: number;
    storesMatched: number;
    storesTriggeredScrape: number;
    sentToSentinel?: number;
    errors: number;
    commentsCapured?: number;       // intentional spelling — matches backend exactly
    commentReviewsCreated?: number;
  };
  cursorUsed?: string;
  cursorAdvancedTo?: string;
  triggeredBy?: "cron" | "manual";
  truncationWarning?: boolean;
  truncationMessage?: string;
  toolCalls: ToolCallLog[];
  error?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
};

export type FlagReason = "wrong-match" | "bad-classification" | "spam";

export type FbReviewStats = {
  totalGroups: number;
  activeGroups: number;
  totalRuns: number;
  totalReviewsCreated: number;
  totalPostsProcessed: number;
  flaggedPosts: number;
};

// API functions

const BASE = "/fb-group-reviews";

export const listGroups = async (): Promise<BackendApiResponse<FacebookGroup[]>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/groups`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch groups" };
  }
};

export const createGroup = async ({
  url,
  name,
  onlyPostsNewerThan,
}: {
  url: string;
  name: string;
  onlyPostsNewerThan?: string;
}): Promise<BackendApiResponse<FacebookGroup>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/groups`,
      { url, name, onlyPostsNewerThan },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to create group" };
  }
};

export const updateGroup = async (
  id: string,
  updates: {
    name?: string;
    status?: string;
    isActive?: boolean;
    notes?: string;
    allowExternalLinksInComments?: boolean;
    scrapeConfig?: {
      maxPosts?: number;
      minLikes?: number;
      onlyPostsNewerThan?: string;
      processComments?: boolean;
      deepScrapeMinComments?: number;
      maxCommentsPerPost?: number;
      maxDeepScrapesPerRun?: number;
    };
    cursor?: { lastPostDate?: string | null };
  }
): Promise<BackendApiResponse<FacebookGroup>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}${BASE}/groups/${id}`,
      updates,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to update group" };
  }
};

export const deleteGroup = async (id: string): Promise<BackendApiResponse<void>> => {
  try {
    const response = await axios.delete(`${getApiBaseUrl()}${BASE}/groups/${id}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete group" };
  }
};

export const triggerScrape = async (
  groupId: string,
  options?: { maxPosts?: number }
): Promise<BackendApiResponse<{ runId: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/groups/${groupId}/scrape`,
      options?.maxPosts ? { maxPosts: options.maxPosts } : {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to trigger scrape" };
  }
};

export const listRuns = async ({
  groupId,
  page = 1,
  limit = 20,
}: {
  groupId?: string;
  page?: number;
  limit?: number;
} = {}): Promise<
  BackendApiResponse<ScrapeRun[]> & {
    pagination?: { current_page: number; total_pages: number; total_items: number };
  }
> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (groupId) params.set("groupId", groupId);
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/runs?${params}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch runs" };
  }
};

export const getRun = async (id: string): Promise<BackendApiResponse<ScrapeRun>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/runs/${id}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch run" };
  }
};

export type RunReview = {
  _id: string;
  externalReviewId?: string;
  externalAuthor?: string;
  externalDate?: string;
  text?: string;
  /**
   * Permanent ImageKit URLs of the post images. Never render sourceData.images instead: those are
   * raw fbcdn URLs that expire (oe= param).
   */
  images?: string[];
  overallRating: number;
  store: {
    _id: string;
    name: string;
    slug: string;
    status?: "draft" | "published";
  } | null;
  scraperJob?: {
    _id: string;
    status: string;
    draftStoreId?: string;
  } | null;
  sourceData?: {
    postUrl?: string;
    postId?: string;
    groupName?: string;
    groupUrl?: string;
    allowExternalLinksInComments?: boolean;
    authorName?: string;
    sentimentLabel?: string;
    isComment?: boolean;
    commentId?: string;
    parentPostId?: string;
    parentPostUrl?: string;
    entryType?: "group" | "standalone";
    triggerSource?: "auto" | "manual";
    // Trackable ytfrg.com short link minted per review for the FB reply comment
    commentShortUrl?: string;
    commentShortCode?: string;
    [key: string]: unknown;
  };
  commented?: { at: string };
  skipped?: { at: string };
  softDeleteReason?: string;
  softDeletedAt?: string;
  createdAt: string;
};

export const getRunReviews = async (runId: string): Promise<BackendApiResponse<RunReview[]>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/runs/${runId}/reviews`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch run reviews" };
  }
};

export type PostReviewStatus = "pending" | "classifying" | "matching" | "creating_review" | "completed" | "skipped" | "failed" | "awaiting_store_confirmation";

export type PostReview = {
  _id: string;
  runId: string;
  groupId: string;
  postId: string;
  postUrl?: string;
  authorName?: string;
  text: string;
  postDate?: string;
  likes?: number;
  commentCount?: number;
  status: PostReviewStatus;
  entryType?: "group" | "standalone";
  triggerSource?: "auto" | "manual";
  sourceType?: 'post' | 'comment';
  parentPostReviewId?: string;
  commentId?: string;
  classification?: {
    isReview: boolean;
    confidence: number;
    storeName?: string;
    numericRating?: number;
    sentimentLabel?: string;
    reviewText?: string;
    isInquiry?: boolean;
  };
  storeMatch?: {
    matched: boolean;
    storeId?: string;
    matchStrategy?: string;
    scraperJobId?: string;
    scraperJobTriggered?: boolean;
  };
  matchedStore?: { name: string; slug: string } | null;
  draftStore?: { name: string; slug: string } | null;
  reviewId?: string;
  flag?: { reason: FlagReason; flaggedAt: string };
  adminNote?: string;
  toolCalls: ToolCallLog[];
  error?: string;
  createdAt: string;
  updatedAt: string;
};

export const getRunPosts = async (
  runId: string,
  { page = 1, limit = 50 }: { page?: number; limit?: number } = {}
): Promise<BackendApiResponse<PostReview[]> & { pagination?: { current_page: number; total_pages: number; total_items: number } }> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/runs/${runId}/posts?${params}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch post reviews" };
  }
};

export type ReconcileResult = {
  checked: number;
  reconciledByJobId: number;
  reconciledByName: number;
  alreadyMatched: number;
};

export const runReconciliation = async (): Promise<BackendApiResponse<ReconcileResult>> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}${BASE}/reconcile`, {}, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Reconciliation failed" };
  }
};

export const getStats = async (): Promise<BackendApiResponse<FbReviewStats>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/stats`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch stats" };
  }
};

export const retryPost = async (postId: string): Promise<BackendApiResponse<{ status: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/posts/${postId}/retry`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to retry post" };
  }
};

export const flagPost = async (postId: string, reason: FlagReason): Promise<BackendApiResponse<{ flagged: boolean }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/posts/${postId}/flag`,
      { reason },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to flag post" };
  }
};

export const unflagPost = async (postId: string): Promise<BackendApiResponse<{ unflagged: boolean }>> => {
  try {
    const response = await axios.delete(`${getApiBaseUrl()}${BASE}/posts/${postId}/flag`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to unflag post" };
  }
};

export const setRunNote = async (runId: string, note: string): Promise<BackendApiResponse<{ updated: boolean }>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}${BASE}/runs/${runId}/note`,
      { note },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to set run note" };
  }
};

export const listAllPosts = async ({
  page = 1,
  limit = 20,
  unmatched = false,
  entryType,
  triggerSource,
}: {
  page?: number;
  limit?: number;
  unmatched?: boolean;
  entryType?: "group" | "standalone";
  triggerSource?: "auto" | "manual";
} = {}): Promise<BackendApiResponse<PostReview[]> & { pagination?: { current_page: number; total_pages: number; total_items: number } }> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (unmatched) params.set("unmatched", "true");
    if (entryType) params.set("entryType", entryType);
    if (triggerSource) params.set("triggerSource", triggerSource);
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/posts?${params}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch posts" };
  }
};

export const deletePostReview = async (postId: string): Promise<BackendApiResponse<void>> => {
  try {
    const response = await axios.delete(`${getApiBaseUrl()}${BASE}/posts/${postId}/review`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to delete review" };
  }
};

export const assignPostToStore = async (
  postId: string,
  storeId: string
): Promise<BackendApiResponse<{ reviewId: string }>> => {
  try {
    const response = await axios.put(
      `${getApiBaseUrl()}${BASE}/posts/${postId}/assign`,
      { storeId },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to assign store" };
  }
};

export type StoreSearchResult = {
  _id: string;
  name: string;
  nameEn?: string;
  slug: string;
  logoUrl?: string;
  status?: string;
};

export const searchStoresForAssignment = async (
  query: string
): Promise<BackendApiResponse<StoreSearchResult[]>> => {
  try {
    const response = await axios.get(
      `${getApiBaseUrl()}${BASE}/stores/search?q=${encodeURIComponent(query)}`,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to search stores" };
  }
};

export const listAllReviews = async ({
  page = 1,
  limit = 20,
  unmatched = false,
  matched,
  hideCommented = false,
  sortOrder = 'desc',
  softDeleted,
  entryType,
  triggerSource,
}: {
  page?: number;
  limit?: number;
  unmatched?: boolean;
  matched?: boolean;
  hideCommented?: boolean;
  sortOrder?: 'asc' | 'desc';
  softDeleted?: boolean;
  entryType?: "group" | "standalone";
  triggerSource?: "auto" | "manual";
} = {}): Promise<BackendApiResponse<RunReview[]> & { pagination?: { current_page: number; total_pages: number; total_items: number } }> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (unmatched) params.set("unmatched", "true");
    if (matched !== undefined) params.set("matched", matched ? "true" : "false");
    if (hideCommented) params.set("hideCommented", "true");
    if (sortOrder !== 'desc') params.set("sortOrder", sortOrder);
    if (softDeleted) params.set("softDeleted", "true");
    if (entryType) params.set("entryType", entryType);
    if (triggerSource) params.set("triggerSource", triggerSource);
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/reviews?${params}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to fetch reviews" };
  }
};

export const assignReview = async (reviewId: string, storeId: string): Promise<BackendApiResponse<null>> => {
  try {
    const response = await axios.put(`${getApiBaseUrl()}${BASE}/reviews/${reviewId}/store`, { storeId }, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to assign store" };
  }
};

export const triggerScrapeForReview = async (reviewId: string, url: string): Promise<BackendApiResponse<{ jobId: string }>> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}${BASE}/reviews/${reviewId}/trigger-scrape`, { url }, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to trigger scraper" };
  }
};

export const skipReview = async (reviewId: string): Promise<BackendApiResponse<{ skipped: boolean }>> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}${BASE}/reviews/${reviewId}/skip`, {}, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to skip review" };
  }
};

export const deleteFbReview = async (reviewId: string): Promise<BackendApiResponse<null>> => {
  try {
    const response = await axios.delete(`${getApiBaseUrl()}${BASE}/reviews/${reviewId}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete review" };
  }
};

export const bulkSoftDeleteReviews = async (
  reviewIds: string[],
  reason?: string
): Promise<BackendApiResponse<{ modified: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/reviews/bulk-soft-delete`,
      { reviewIds, reason },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to soft-delete reviews" };
  }
};

export const bulkRestoreReviews = async (
  reviewIds: string[]
): Promise<BackendApiResponse<{ modified: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/reviews/bulk-restore`,
      { reviewIds },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to restore reviews" };
  }
};

export const bulkDeleteReviews = async (
  reviewIds: string[]
): Promise<BackendApiResponse<{ deleted: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/reviews/bulk-delete`,
      { reviewIds },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to delete reviews" };
  }
};

export const markReviewAsCommented = async (reviewId: string): Promise<BackendApiResponse<{ commented: boolean }>> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}${BASE}/reviews/${reviewId}/commented`, {}, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to mark as commented" };
  }
};

export type CommentLink = {
  shortUrl: string | null;
  shortCode: string | null;
  longUrl: string;
  degraded: boolean;
  degradedReason?: string;
};

export const mintCommentLink = async (reviewId: string): Promise<BackendApiResponse<CommentLink>> => {
  try {
    const response = await axios.post(`${getApiBaseUrl()}${BASE}/reviews/${reviewId}/comment-link`, {}, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    return { ok: false, error: "Failed to mint comment link" };
  }
};

export const scrapePostComments = async (postReviewId: string): Promise<BackendApiResponse<{ status: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/posts/${postReviewId}/scrape-comments`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch {
    return { ok: false, error: 'Failed to trigger comment scrape' };
  }
};

// ─── Standalone Post (Phase 23) ───

export type StandaloneCandidate = {
  postReviewId: string;
  candidateStore: {
    matched: boolean;
    storeId?: string;
    matchStrategy?: string;
    storeName?: string;
    storeNameEn?: string;
    storeSlug?: string;
  };
  postText: string;
  lowConfidence?: boolean;
  postIsInquiry?: boolean;
};

export const createStandalonePost = async ({
  postUrl,
  postText,
  postAuthorName,
}: {
  postUrl: string;
  postText?: string;
  postAuthorName?: string;
}): Promise<BackendApiResponse<StandaloneCandidate>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/standalone-post`,
      { postUrl, postText, postAuthorName },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to create standalone post" };
  }
};

export const confirmStandalonePost = async (
  id: string,
  storeId: string
): Promise<BackendApiResponse<{ status: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/standalone-post/${id}/confirm`,
      { storeId },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to confirm standalone post" };
  }
};

// ─── Pipeline Lab types ───

export type ClassifyDryRunItem = {
  postId: string;
  text: string;
  authorName?: string;
  originalClassification?: {
    isReview: boolean;
    confidence: number;
    storeName?: string;
    numericRating?: number;
    sentimentLabel?: string;
    reviewText?: string;
    storeLinks?: string[];
  };
  newClassification?: {
    isReview: boolean;
    confidence: number;
    storeName?: string;
    numericRating?: number;
    sentimentLabel?: string;
    reviewText?: string;
    storeLinks?: string[];
  };
  wouldBeReview: boolean;
  error?: string;
  durationMs: number;
};

export type ClassifyDryRunResult = {
  results: ClassifyDryRunItem[];
  systemPromptUsed: string;
};

export type SerperResult = {
  title: string;
  link: string;
  snippet?: string;
};

export type MatchStep =
  | { step: "url_match"; linksChecked: string[]; matched: false }
  | { step: "url_match"; linksChecked: string[]; matched: true; storeId: string; storeName: string }
  | { step: "name_match"; query: string | null; matched: false }
  | { step: "name_match"; query: string | null; matched: true; storeId: string; storeName: string }
  | {
      step: "serper_search";
      query: string | null;
      rawResults: SerperResult[];
      selectedUrl: string | null;
      matched: false;
      wouldTriggerScraper: boolean;
      skippedReason?: string;
    }
  | {
      step: "serper_search";
      query: string | null;
      rawResults: SerperResult[];
      selectedUrl: string;
      matched: true;
      matchedStore: { storeId: string; storeName: string };
      wouldTriggerScraper: false;
    };

export type MatchDryRunResult = {
  steps: MatchStep[];
  outcome: "matched" | "would_trigger_scraper" | "sentinel";
  matchedStore?: { storeId: string; storeName: string };
  matchedAt?: "url" | "name" | "search";
  triggeredUrl?: string;
  serperQuery?: string;
  durationMs: number;
};

// ─── Pipeline Lab API ───

export const getPipelineLabPrompt = async (): Promise<
  BackendApiResponse<{ systemPrompt: string; userPromptTemplate: string }>
> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/pipeline-lab/prompt`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch {
    return { ok: false, error: "Failed to fetch pipeline lab prompt" };
  }
};

export const dryRunClassify = async ({
  postReviewIds,
  rawPosts,
  customSystemPrompt,
  customUserPromptTemplate,
}: {
  postReviewIds?: string[];
  rawPosts?: { postId: string; text: string; authorName?: string; likes?: number; commentCount?: number }[];
  customSystemPrompt?: string;
  customUserPromptTemplate?: string;
}): Promise<BackendApiResponse<ClassifyDryRunResult>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/pipeline-lab/classify`,
      { postReviewIds, rawPosts, customSystemPrompt, customUserPromptTemplate },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Dry run classify failed" };
  }
};

export const dryRunMatch = async ({
  storeName,
  storeLinks,
}: {
  storeName?: string | null;
  storeLinks?: string[];
}): Promise<BackendApiResponse<MatchDryRunResult>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/pipeline-lab/match`,
      { storeName, storeLinks },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    return { ok: false, error: "Dry run match failed" };
  }
};

// ─── Eval Suite types ───

export type EvalCase = {
  id: string;
  description: string;
  tags?: string[];
  input: {
    text: string;
    authorName?: string;
    likes?: number;
    commentCount?: number;
  };
  expected: {
    isReview: boolean;
    storeName?: string;
    sentimentLabel?: string;
  };
};

export type EvalAssertions = {
  isReview: "pass" | "fail";
  storeName?: "pass" | "fail" | "skip";
  sentimentLabel?: "pass" | "fail" | "skip";
};

export type EvalCaseResult = {
  id: string;
  description: string;
  expected: EvalCase["expected"];
  actual: {
    isReview: boolean;
    confidence: number;
    storeName?: string;
    sentimentLabel?: string;
    reviewText?: string;
  } | null;
  assertions: EvalAssertions;
  error?: string;
  durationMs: number;
};

export type EvalRunResult = {
  modelId: string;
  systemPromptUsed: string;
  results: EvalCaseResult[];
  scores: {
    isReview: { correct: number; total: number };
    storeName: { correct: number; total: number } | null;
    sentimentLabel: { correct: number; total: number } | null;
  };
  durationMs: number;
};

export type EvalModel = {
  id: string;
  label: string;
};

// ─── Eval Suite API ───

export const getEvalCases = async (): Promise<
  BackendApiResponse<{ version: number; cases: EvalCase[] }>
> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/pipeline-lab/evals`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch {
    return { ok: false, error: "Failed to fetch eval cases" };
  }
};

export const getEvalModels = async (): Promise<BackendApiResponse<EvalModel[]>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/pipeline-lab/models`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch {
    return { ok: false, error: "Failed to fetch eval models" };
  }
};

export const runEval = async ({
  modelId,
  customSystemPrompt,
}: {
  modelId: string;
  customSystemPrompt?: string;
}): Promise<BackendApiResponse<EvalRunResult>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/pipeline-lab/run-eval`,
      { modelId, customSystemPrompt },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Eval run failed" };
  }
};

// ─── Inquiries admin tab (quick-260912-kjm) ───

export type InquiryStoreSummary = {
  id: string;
  name: string;
  slug?: string;
  status?: string;
  flaggedFraud: boolean;
  reviewCount: number;
  avgRating: number;
  flaggedReviewCount: number;
};

// quick 261002-c0y: one active store linked to an inquiry (server-built, primary first). Pending
// entries are scrapes still running; `store` is absent for them.
export type InquiryLinkedStoreView = {
  storeId?: string;
  mention?: string;
  // "vision": linked or scraped from the screenshot identity card (Phase 50).
  source: "auto" | "moderator" | "scrape" | "vision";
  scraperJobId?: string;
  pending: boolean;
  jobStatus?: string;
  store?: InquiryStoreSummary;
};

export type InquiryItem = {
  id: string;
  postId: string;
  postUrl?: string;
  authorName?: string;
  text: string;
  postDate?: string;
  groupId?: string;
  groupName?: string;
  classification?: {
    storeName?: string;
    storeLinks?: string[];
    confidence?: number;
    isInquiry?: boolean;
    isReview?: boolean;
    otherBusinesses?: Array<{ name: string; mentionType?: string }>;
  };
  storeMatch?: {
    matched: boolean;
    storeId?: string;
    matchStrategy?: string;
    scraperJobId?: string;
    scraperJobTriggered?: boolean;
  };
  inquiryReply?: { text: string; generatedAt: string; model: string; storeId: string; storeIds?: string[] };
  inquiryAction?: { action: "commented" | "skipped"; at: string };
  store?: InquiryStoreSummary;
  // Optional: tolerates a cached list from before quick 261002-c0y.
  linkedStores?: InquiryLinkedStoreView[];
  storesChangedSinceReply?: boolean;
  // Phase 50 (SSM-15): screenshot identity card kind, derived server-side from vision.route.outcome,
  // and the moderator's recorded card verdict when there is one.
  visionCard?: "candidates" | "refused" | "none" | "conflict";
  identityVerdict?: string;
  // Optional — tolerates stale cache shape from before quick-260912-q7m
  comments?: {
    captured: number;
    scrapedAt?: string;
    extractedItems: number;
    reviewsFromComments: number;
  };
  createdAt: string;
};

export type InquiryStats = {
  total: number;
  matched: number;
  unmatched: number;
  replied: number;
  commented: number;
  skipped: number;
  withComments: number;
  commentsPending: number;
  commentsMined: number;
  withCommentReviews: number;
};

export type InquiryListResponse = {
  items: InquiryItem[];
  total: number;
  page: number;
  limit: number;
};

export const getInquiries = async ({
  page = 1,
  limit = 25,
  matched,
  replied,
  action,
  groupId,
  hasComments,
  commentsMined,
  commentReviews,
}: {
  page?: number;
  limit?: number;
  matched?: "true" | "false";
  replied?: "true" | "false";
  action?: "commented" | "skipped" | "none";
  groupId?: string;
  hasComments?: "true" | "false";
  commentsMined?: "pending" | "mined";
  commentReviews?: "true" | "false";
} = {}): Promise<BackendApiResponse<InquiryListResponse>> => {
  try {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (matched) params.set("matched", matched);
    if (replied) params.set("replied", replied);
    if (action) params.set("action", action);
    if (groupId) params.set("groupId", groupId);
    if (hasComments) params.set("hasComments", hasComments);
    if (commentsMined) params.set("commentsMined", commentsMined);
    if (commentReviews) params.set("commentReviews", commentReviews);
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/inquiries?${params}`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch {
    return { ok: false, error: "Failed to fetch inquiries" };
  }
};

export const getInquiryStats = async (): Promise<BackendApiResponse<InquiryStats>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/inquiries/stats`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch {
    return { ok: false, error: "Failed to fetch inquiry stats" };
  }
};

export const generateInquiryReply = async (
  id: string
): Promise<BackendApiResponse<{ inquiryReply: InquiryItem["inquiryReply"] }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/inquiries/${id}/generate-reply`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to generate reply" };
  }
};

export const createStoreForInquiry = async (
  id: string
): Promise<BackendApiResponse<{ scraperJobId: string }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/inquiries/${id}/create-store`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to trigger store creation" };
  }
};

export const setInquiryAction = async (
  id: string,
  action: "commented" | "skipped"
): Promise<BackendApiResponse<{ inquiryAction: InquiryItem["inquiryAction"] }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/inquiries/${id}/action`,
      { action },
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to set inquiry action" };
  }
};

export const clearInquiryAction = async (
  id: string
): Promise<BackendApiResponse<{ cleared: true }>> => {
  try {
    const response = await axios.delete(`${getApiBaseUrl()}${BASE}/inquiries/${id}/action`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to clear inquiry action" };
  }
};

export const rematchInquiry = async (
  id: string
): Promise<BackendApiResponse<{ storeMatch: InquiryItem["storeMatch"] }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/inquiries/${id}/rematch`,
      {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to re-run store matching" };
  }
};

// ─── Bulk retry of failed posts (quick-260922-h9i) ───

export type BulkRetrySelector = {
  runIds?: string[];
  errorContains?: string;
  createdAfter?: string;
  createdBefore?: string;
};

export type BulkRetryPreview = {
  matched: number;
  byRun: { runId: string; count: number }[];
  byError: { error: string; count: number }[];
};

export type BulkRetryStatus = {
  batchId: string | null;
  counts: { queued: number; running: number; done: number; cancelled: number };
  consecutiveFailures: number;
  breakerTripped: boolean;
};

export const previewBulkRetry = async (
  selector: BulkRetrySelector
): Promise<BackendApiResponse<BulkRetryPreview>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/bulk-retry/preview`,
      selector,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to preview bulk retry" };
  }
};

export const startBulkRetry = async (
  selector: BulkRetrySelector & { limit?: number }
): Promise<BackendApiResponse<{ batchId: string; queued: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/bulk-retry`,
      selector,
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to start bulk retry" };
  }
};

export const cancelBulkRetry = async (
  batchId?: string
): Promise<BackendApiResponse<{ cancelled: number }>> => {
  try {
    const response = await axios.post(
      `${getApiBaseUrl()}${BASE}/bulk-retry/cancel`,
      batchId ? { batchId } : {},
      { headers: getHeaders() }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to cancel bulk retry" };
  }
};

export const getBulkRetryStatus = async (): Promise<BackendApiResponse<BulkRetryStatus>> => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}${BASE}/bulk-retry`, {
      headers: getHeaders(),
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      return { ok: false, error: error.response.data.error };
    }
    return { ok: false, error: "Failed to get bulk retry status" };
  }
};
